import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { compose } from 'redux';

import MediaQueryContainer from '../../containers/media-query-container/media-query-container';
import QueryParamManager from '../../containers/query-param-manager/query-param-manager';
import AuthenticationWrapped from '../../containers/authentication/authentication';

import {
  dataDependencyTypePicker,
  dataDependencyIdsAdder,
  stringInputSpec,
  stringSelectSpec,
  boolCheckboxSpec,
  repeatable,
} from '../../lib/editing-configs';

const Page = ({
  className,
  children,
  history,
  queryParams,
  watchQueryParams,
  requiresAuth,
  authRoleRequired,
  loginModalCopyOverride,
  loginPageCopyOverride,
}) => (
  <div className={className}>
    <MediaQueryContainer />
    <QueryParamManager
      watchQueryParams={watchQueryParams}
      queryParams={queryParams}
    />
    <AuthenticationWrapped
      requiresAuth={requiresAuth}
      authRoleRequired={authRoleRequired}
      loginModalCopyOverride={loginModalCopyOverride}
      loginPageCopyOverride={loginPageCopyOverride}
      onSuccess={history.location.pathname}
    >
      {children}
    </AuthenticationWrapped>
  </div>
);

Page.propTypes = {
  className: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.array]),
  history: PropTypes.object,
  queryParams: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  watchQueryParams: PropTypes.bool,
  includeInactiveOrNonMonetizingProducts: PropTypes.bool,
  requiresAuth: PropTypes.bool,
  authRoleRequired: PropTypes.string,
  loginModalCopyOverride: PropTypes.string,
  loginPageCopyOverride: PropTypes.string,
};

Page.displayName = 'Page';

Page.defaultProps = {
  className: null,
  children: null,
  history: null,
  requiresAuth: false,
  authRoleRequired: 'nerdwallet/logged_in',
};

Page.getEditingConfig = (componentDocument) => {
  const authRoleContent =
    componentDocument?.props?.requiresAuth === false
      ? []
      : [
          stringInputSpec(
            'authRoleRequired',
            'Role required for authentication',
            {
              help: 'The role that is required for authentication. Please note: if you choose to require authentication for a page, this defaults to "nerdwallet/logged_in", meaning the page will be configured to require users to login to view the page\'s contents. If the page requires a more targeted role for authentication, please specify here. Also, please note that all pages that require authentication can be viewed by NerdWallet employees (eg: users who possess the "nerdwallet/employee" role).',
            }
          ),
          stringInputSpec(
            'loginPageCopyOverride',
            'Login splash page custom copy',
            {
              help: "If you'd like to output custom copy on the login splash page, please locate your custom output in this field.",
            }
          ),
          stringInputSpec('loginModalCopyOverride', 'Custom login modal copy', {
            help: "If you'd like to output custom copy in the login modal, please locate your custom output in this field.",
          }),
        ];
  const themedHeaderConfig = componentDocument?.meta?.needsGlobalNav
    ? [
        stringSelectSpec(
          'meta.theme',
          'Select the NerdWallet nav type',
          [
            { label: 'Default', value: 'default' },
            { label: 'Company', value: 'company' },
          ],
          {
            isProp: false,
            help: 'Note: requires a save to take effect.',
          }
        ),
      ]
    : [];
  return [
    // Page meta info
    boolCheckboxSpec('meta.needsGlobalNav', 'Use NerdWallet nav', {
      isProp: false,
      help: 'Note: requires a save to take effect.',
    }),

    ...themedHeaderConfig,

    // Builder query params
    repeatable('queryParams', 'Current Query Params', 'Query param', () => [
      stringInputSpec('name', 'Query Param Name'),
      stringInputSpec('value', 'Query Param Value'),
    ]),
    boolCheckboxSpec('watchQueryParams', 'Filter components by query params?', {
      help: 'Some components may appear on the page only when certain query params are present. When this box is unchecked, all components will appear in the builder, regardless of whether the query param that makes them visible is present.',
    }),

    // Data dependencies
    // This field is read from the JSON and passed to the providers on creation in client/routes/container.jsx.
    boolCheckboxSpec(
      'includeInactiveOrNonMonetizingProducts',
      'Include inactive or non-monetizing products on page?',
      {
        help: 'Products will appear on the page regardless of whether they are currently active on NerdWallet. Inactive products will come with messaging indicating them as such. You may need to save and refresh the page to see this change take effect.',
      }
    ),
    dataDependencyTypePicker,
    dataDependencyIdsAdder,
    stringInputSpec('dataDependency.data.slug', 'Slug', { isProp: false }),
    boolCheckboxSpec('requiresAuth', 'Page requires user authentication', {
      help: 'In order to view page, user must be signed in and have the required role/scope',
    }),
    ...authRoleContent,
  ];
};

Page.isContainer = true;

export default compose(withRouter, connect())(Page);
